import React, { useId } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDrawer, LntForm, LntFormItem, LntSelect } from '@app/components/base'

import { LOCATION_KEY, MONITORING_VALIDATION_KEY } from '@app/constants/queryKeys'
import { SIZE } from '@app/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'

import { postMonitoringValidationConfigSpecialCases } from '@app/api/routes/monitoringValidation'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getCompetitorsNetworks } from '@app/api/routes/locationCompetitorsNetworks'

import { useNotification } from '@app/hooks'

import { getFilterFromEntities } from '@app/utils/entitiesAdapters'

import styles from './styles.module.scss'

import type { AddAbnormalPriceDetectionDrawerProps } from './types'
import type { PostMonitoringValidationConfigSpecialCasesData } from '@app/api/routes/monitoringValidation/types'
import type { LntSelectProps } from '@app/components/base/Select/types'

const commonSelectProps: LntSelectProps = {
  showArrow: true,
  size: SIZE.middle,
  strictValue: true
}

export const AddAbnormalPriceDetectionDrawer: React.FC<AddAbnormalPriceDetectionDrawerProps> = props => {
  const { onClose } = props

  const formId = useId()
  const form = useForm<PostMonitoringValidationConfigSpecialCasesData>()

  const queryClient = useQueryClient()
  const notification = useNotification()

  const postSpecialCasesMutation = useMutation({
    mutationFn: postMonitoringValidationConfigSpecialCases,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.specialCases] })

      notification.success({
        message: 'Связь успешно добавлена.'
      })

      onClose?.()
    }
  })

  const handleSubmit = (data: PostMonitoringValidationConfigSpecialCasesData): void => {
    postSpecialCasesMutation.mutate(data)
  }

  const footerButtons: React.ReactNode = (
    <div className={ styles.addAbnormalPriceDetectionDrawer__footer }>
      <LntButton
        htmlType='submit'
        form={ formId }
      >
        Сохранить
      </LntButton>
    </div>
  )

  return (
    <LntDrawer
      { ...props }
      title='Добавление исключения'
      width={ 424 }
      footer={ footerButtons }
    >
      <LntForm<PostMonitoringValidationConfigSpecialCasesData>
        layout='horizontal'
        formInstance={ form }
        onSubmit={ handleSubmit }
        id={ formId }
      >
        <div className={ styles.addAbnormalPriceDetectionDrawerForm__itemsWrapper }>
          <LntFormItem
            name='salesOrganizationId'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
                queryFn: getSalesOrganization,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>
          <LntFormItem
            name='clusterGroupId'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationClusterGroupsItems],
                queryFn: getClusterGroups,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>
          <LntFormItem
            name='clusterId'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationClustersItems],
                queryFn: getClusters,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>
          <LntFormItem
            name='competitorNetworkId'
            defaultValue={ [] }
          >
            <LntSelect
              { ...commonSelectProps }
              placeholder={ FILTERS_PLACEHOLDERS.competitorsNetworksFilter }
              querySelect={ {
                queryKey: [LOCATION_KEY.locationCompetitorsItems],
                queryFn: getCompetitorsNetworks,
                select: getFilterFromEntities
              } }
            />
          </LntFormItem>
        </div>
      </LntForm>
    </LntDrawer>
  )
}
